// plan-03 S07 + plan-10 S05: hash routing + URL params — bible §31

import { parseHash, parseQuery } from '../core/url'

type DeferredCommand = { cmd: string; args: string[] } | { stageActivate: string; stageArgs: string[] }

export function resolveHashCommands(): DeferredCommand[] {
  const hash = parseHash()
  const query = parseQuery()
  const commands: DeferredCommand[] = []

  // plan-10 S05: ?stage=<spec> takes priority, then #stage=<spec>
  const stageSpec = query.stage ?? hash.stage
  if (stageSpec) {
    const parsed = parseStageSpec(stageSpec)
    if (parsed) {
      commands.push(parsed)
    }
  }

  if (hash.pulse48) {
    commands.push({ cmd: '48', args: [] })
  }
  if (hash.palimpsest) {
    commands.push({ cmd: 'palimpsest', args: [] })
  }
  if (hash.heatmap) {
    // legacy alias: #heatmap=<word> → activate heatmap with word
    commands.push({ stageActivate: 'heatmap', stageArgs: [hash.heatmap] })
  }
  if (hash.divine) {
    // legacy alias: #divine=<hash> → divine command (streams + activates stage)
    commands.push({ cmd: 'divine', args: [hash.divine] })
  }
  if (hash.portal) {
    // legacy alias: #portal → engage
    commands.push({ cmd: 'engage', args: [] })
  }
  // plan-13 S07: broadcast + nodes hash routes
  if (hash.broadcast) {
    if (hash.broadcastDate) {
      commands.push({ cmd: 'replay', args: [hash.broadcastDate] })
    } else {
      commands.push({ cmd: 'tune', args: [] })
    }
  }
  if (hash.nodes) {
    commands.push({ stageActivate: 'nodes', stageArgs: [] })
  }
  // plan-18 S05: commune hash routes
  if (hash.commune) {
    if (hash.commune.startsWith('q:')) {
      // text form: decode base32 question, submit as commune command
      const b32 = hash.commune.slice(2)
      try {
        const question = atob(b32.replace(/-/g, '+').replace(/_/g, '/'))
        commands.push({ cmd: 'commune', args: [question] })
      } catch {
        // invalid base32, ignore
      }
    } else {
      // hash form: activate commune widget with hash
      commands.push({ cmd: 'commune', args: [`hash:${hash.commune}`] })
    }
  }
  // plan-20 S04: #vague activates the vague-ifier; #vague=mirror starts the mirror
  if (hash.vague !== null) {
    commands.push({ cmd: 'vague', args: hash.vague ? [hash.vague] : [] })
  }
  // plan-31 / §46: #oracle (idle) or #oracle=q:<base32> (summon decoded question).
  // Non-replay — the oracle is non-deterministic; no hash form.
  if (hash.oracle !== null) {
    if (hash.oracle.startsWith('q:')) {
      const b32 = hash.oracle.slice(2)
      try {
        const question = atob(b32.replace(/-/g, '+').replace(/_/g, '/'))
        commands.push({ cmd: 'oracle', args: [question] })
      } catch {
        // invalid base32 — fall through to idle
        commands.push({ cmd: 'oracle', args: [] })
      }
    } else {
      commands.push({ cmd: 'oracle', args: [] })
    }
  }

  return commands
}

// Parse stage spec: "directory", "heatmap=king", "divine=abc123", "portal"
function parseStageSpec(spec: string): DeferredCommand | null {
  const parts = spec.split('=')
  const widgetId = parts[0]
  const widgetArg = parts.length > 1 ? parts.slice(1).join('=') : ''

  switch (widgetId) {
    case 'directory':
      return { cmd: 'directory', args: [] }
    case 'heatmap':
      if (widgetArg) {
        return { cmd: 'consensus', args: [widgetArg] }
      }
      return { stageActivate: 'heatmap', stageArgs: [] }
    case 'divine':
      if (widgetArg) {
        return { cmd: 'divine', args: [widgetArg] }
      }
      return { cmd: 'divine', args: [] }
    case 'portal':
      return { cmd: 'engage', args: [] }
    default:
      return null
  }
}
